import { identity, is } from 'ramda'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { Grid } from '../../structure/Grid'
import { SmallText } from '../../../../../components/presentation/Text'
import { generateIsPickerSelected, generateOnPickerChange, getOptionValue } from './_helpers'
import { hasValue } from '../../../../../utils/helpers/value'
import { useLazyOptions } from '../../../utils/helpers/options'
import LabelIconListItem from '../../list/LabelIconListItem'
import Line from '../../helpers/Line'
import Panel from '../../structure/Panel'
import LineSeparator from '../../helpers/LineSeparator'
import LabelListItem from '../../list/LabelListItem'

const DefaultSelector = ({
  t,
  option,
  isSelected,
  isCurrent,
  multiple,
  labelField,
  descriptionField,
  tooltipField,
  disableItem,
  reasonForDisablePresenter,
  ...props
}) => {
  const isDisabled = disableItem && disableItem(option)

  let bgColor = 'white'
  if (isSelected) bgColor = 'light1'
  if (isCurrent) bgColor = 'light2'
  if (isDisabled) bgColor = 'light3'

  const icon = isDisabled
    ? 'RiAlertLine'
    : option?.rightIcon || (isSelected ? 'RiCheckboxCircleLine' : 'RiCheckboxBlankCircleLine')

  const iconColor = isDisabled ? 'primary' : !isSelected ? 'light2' : 'primary'

  return (
    <LabelIconListItem
      option={option}
      reasonForDisablePresenter={reasonForDisablePresenter}
      disabled={isDisabled}
      label={t(getOptionValue(option, labelField))}
      description={getOptionValue(option, descriptionField, true)}
      tooltip={getOptionValue(option, tooltipField)}
      icon={icon}
      leftIcon={option?.leftIcon}
      color="dark3"
      bold={isSelected}
      iconColor={iconColor}
      bgColor={bgColor}
      hoverBgColor="light1"
      {...props}
    />
  )
}

const GroupSeparator = ({ option, index, lastOption }) => {
  if (!option?.group || !is(String, option?.group)) return false
  const lastGroup = lastOption?.group
  if (lastGroup === option?.group) return false

  return (
    <Panel bgColor="light1" padding={0.1} noRadius noBorder noMargin algin="center">
      <SmallText size="xxsmall" bold align="center" color="dark2">
        {option.group}
      </SmallText>
    </Panel>
  )
}

const Picker = ({
  options,
  useEmptyOption,
  onChange,
  value = [],
  multiple,
  idField,
  labelField,
  Selector,
  header,
  descriptionField,
  tooltipField,
  currentIndex,
  t: tProp,
  selectOnlyId,
  hideSelecteds,
  disableItem,
  reasonForDisablePresenter,
  hasSelectAll,
  onSelectAll,
  ...props
}) => {
  const { t: tCommon } = useTranslation('common')
  const t = tProp || identity
  const handleChange = generateOnPickerChange(value, onChange, { multiple, idField, selectOnlyId })
  const isSelected = generateIsPickerSelected(value, { multiple, idField })
  const [{ localOptions }] = useLazyOptions(options)

  Selector = Selector || DefaultSelector

  const hasEmptyOption = useEmptyOption && hasValue(value)
  const hasSelectedAll = Array.isArray(value) && value?.length > 0 && value.find(item => !item[labelField])
  const showSelectAll = hasSelectAll && multiple

  const handleSelectAll = () => {
    if (onSelectAll && typeof onSelectAll === 'function') {
      onSelectAll()
    }
  }

  return (
    <Grid wrap direction="column" justify="flex-start" align="stretch" {...props}>
      {header}

      {(hasEmptyOption || showSelectAll) && (
        <>
          {hasEmptyOption && (
            <Selector
              option={undefined}
              onClick={() => onChange(multiple ? [] : undefined)}
              Selector={Selector}
              color="dark5"
              label={`--${tCommon('clearSelection')}--`}
              t={t}
            />
          )}

          {showSelectAll && (
            <Selector
              option={{ id: 'select-all', name: `--${tCommon('selectAll')}--` }}
              onClick={handleSelectAll}
              Selector={Selector}
              color="dark5"
              icon="RiCheckboxCircleLine"
              iconColor="primary"
              label={`--${tCommon('selectAll')}--`}
              t={t}
            />
          )}
        </>
      )}

      {(!!header || hasEmptyOption || showSelectAll) && <Line color="light4" />}

      {!!hideSelecteds ||
        (Array.isArray(value) && !!value?.length && (
          <Panel padding={0.1} noRadius noBorder noMargin algin="center">
            <GroupSeparator
              option={{
                group: value.length > 1 ? tCommon('selected_plural') : tCommon('selected')
              }}
            />
            {hasSelectedAll ? (
              <LabelListItem label={tCommon('selected_plural', { count: value.length })} />
            ) : (
              value.map(
                item =>
                  item[labelField] && (
                    <>
                      <Selector
                        t={t}
                        option={item}
                        key={getOptionValue(item, idField || 'id')}
                        isSelected={isSelected(item)}
                        isCurrent={false}
                        multiple={multiple}
                        onClick={() => handleChange(item)}
                        Selector={Selector}
                        idField={idField}
                        labelField={labelField}
                        descriptionField={descriptionField}
                        disableItem={disableItem}
                        reasonForDisablePresenter={reasonForDisablePresenter}
                      />
                    </>
                  )
              )
            )}

            {!hasSelectedAll && <LineSeparator color="light2" weight={2} />}
          </Panel>
        ))}

      {localOptions.map((item, index) => (
        <>
          <GroupSeparator key={index} option={item} lastOption={localOptions[index - 1]} />
          <Selector
            t={t}
            option={item}
            key={getOptionValue(item, idField || 'id')}
            isSelected={isSelected(item)}
            isCurrent={index === currentIndex}
            multiple={multiple}
            onClick={() => handleChange(item)}
            Selector={Selector}
            idField={idField}
            labelField={labelField}
            descriptionField={descriptionField}
            disableItem={disableItem}
            tooltipField={tooltipField}
            reasonForDisablePresenter={reasonForDisablePresenter}
          />
        </>
      ))}
    </Grid>
  )
}

export default Picker
